import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Updater } from "use-immer";

import {
  IgButton,
  IgCheckbox,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { CustomEventDto, CustomEventFeType } from "@intelligems/ig-types";
import { Grid, useTheme } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { RenderCustomEventIcon } from "@src/features/customEvents/utils";
import { initCustomEvent } from "@src/store/slices/customEvents";
import { selectedExperience } from "@src/store/slices/experiences";

interface CustomEventItemProps {
  event: CustomEventDto;
  isChecked: boolean;
  onSelect: (id: string, checked: boolean) => void;
  setIsCreating: Updater<boolean>;
  setSubpageSelected: Updater<CustomEventFeType | null>;
  setDrawerPage: Updater<string>;
}

export const CustomEventItem = ({
  event,
  onSelect,
  isChecked,
  setIsCreating,
  setSubpageSelected,
  setDrawerPage,
}: CustomEventItemProps) => {
  const theme = useTheme();
  const canAddMetricPostLaunch = useConfiguration<boolean>(
    "add_custom_metric_post_launch"
  )?.value;
  const isAdmin = useIsAdmin();
  const experience = useSelector(selectedExperience);
  const dispatch = useDispatch();
  const isDisabled =
    experience &&
    experience.status !== "pending" &&
    event.type !== "collectionPageView" &&
    event.type !== "productPageView" &&
    event.type !== "pageView" &&
    (canAddMetricPostLaunch || isAdmin
      ? false
      : event.createdAtTs > experience.startedAtTs!) &&
    !event.archivedAtTs;

  return (
    <Grid
      container
      direction={"row"}
      justifyContent={"space-between"}
      key={event.id}
      alignItems={"center"}
      wrap={"nowrap"}
      sx={{
        marginTop: "20px",
      }}
    >
      <Grid
        item
        container
        direction={"row"}
        alignItems={"center"}
        wrap={"nowrap"}
      >
        <IgTooltip
          title={
            isDisabled
              ? "Custom events created after a test has been started cannot be added."
              : ""
          }
        >
          <IgCheckbox
            disabled={isDisabled}
            checked={isChecked}
            sx={{ marginRight: "10px" }}
            onChange={(e, checked) => {
              onSelect(event.id, checked);
            }}
          />
        </IgTooltip>
        {<RenderCustomEventIcon eventType={event.type} />}
        <Grid container direction={"column"} sx={{ marginLeft: "15px" }}>
          <Grid container>
            <IgTypography>{event.name}</IgTypography>
          </Grid>
          <Grid container>
            <IgTypography
              variant={"body2"}
              color={theme.palette.text.secondary}
            >
              {event.description}
            </IgTypography>
          </Grid>
        </Grid>
      </Grid>
      <Grid container item wrap={"nowrap"} xs={2.5}>
        <IgButton
          size={"small"}
          onClick={() => {
            dispatch(initCustomEvent({ customEvent: event }));
            setIsCreating(true);
            setSubpageSelected(event.type as CustomEventFeType);
            setDrawerPage("event");
          }}
        >
          View & Edit
        </IgButton>
      </Grid>
    </Grid>
  );
};
